import { EnvelopeIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { setLoanDetail } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { BaseFile, EmailValue, InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addCreditDenialPdf,
  addNewNote,
  downloadCreditDenialPdf,
  downloadFile,
  getCreditDenial,
  updateCreditDenial,
} from 'services'
import { Checkbox, Select } from 'stories/components'
import { openAuditLog, phoneConvertor, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { PartyModal } from '../Parties/Modal/PartyModal'
import { AddNote } from '../Servicing'

const defaultFederalAgency =
  'Federal Trade Commission\nConsumer Response Center\n600 Pennsylvania Avenue NW\nWashington, DC 20580'
export default function CreditDenial() {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [data, setData] = useState<Record<string, any>>({})
  const [isEmailOpen, setIsEmailOpen] = useState(false)
  const [showPartyModal, setShowPartyModal] = useState(false)
  const [printEmailItem, setPrintEmailItem] = useState<Record<string, any>>({})

  const loanNumber = setLoanNumber()
  const { loanDetail } = useSelector((state: any) => {
    return {
      loanDetail: state.loanDetail,
    }
  })
  const lender = useMemo(() => {
    return loanDetail.closeParties?.Lender || {}
  }, [loanDetail])
  const dispatch = useDispatch()

  useEffect(() => {
    getCreditDenial().then(({ inputs, data }: { inputs: Record<string, InputType>; data: Record<string, any> }) => {
      Object.keys(data).forEach((key) => {
        if (inputs[key]) inputs[key].value = data[key]
      })
      Object.keys(inputs).forEach((key) => {
        inputs[key].history = true
      })
      setInputs(inputs)
      setData(data)
    })
  }, [])

  const onChange = async (key: string, value: any) => {
    try {
      const temp = cloneDeep(inputs)
      temp[key].value = value
      setInputs(temp)
    } catch (e) {}

    if (['CreditScoreObtained', 'CreditScoreType', 'federalAgency'].includes(key)) {
      const tempData = cloneDeep(data)
      tempData[key] = value
      setData(tempData)
      await onBlur(key, value)
    }
  }

  const onBlur = async (key: string, _value?: any) => {
    const value = _value || inputs[key]?.value
    if (_value == undefined && data[key] == value) return

    const newData = cloneDeep(data)
    newData[key] = value
    setData(newData)

    setLoading(true)
    await updateCreditDenial(key, value)
    setLoading(false)
  }

  const onShowHistory = (key: string) => {
    const options: Record<string, any> = {
      table: 'Statement of Credit Denial',
      field: key,
      fieldLabel: inputs[key].title,
      keys: { field: key, loanNumber },
    }
    if (inputs[key].options) options.map = inputs[key].options
    openAuditLog(options)
  }

  const onFill = (key: string) => {
    let val = ''
    if (key === 'AccountDescription') val = 'Application for a mortgage loan'
    if (key === 'ActionTakenDescription') val = 'We regret that we are unable to approve your application'
    const temp = cloneDeep(inputs)
    temp[key].value = val
    setInputs(temp)
  }

  const onSubmit = async (
    subject: string,
    content: string,
    files: BaseFile[],
    notifiers: EmailValue,
    isEmail: boolean,
    replyData: Record<string, any>,
    notePageType: any,
    links: any[] = [],
  ) => {
    await addNewNote(loanNumber, subject, content, files, notifiers, isEmail, replyData, notePageType, links)

    setIsEmailOpen(false)
  }

  const onDownload = async () => {
    setLoading(true)
    const pdfData = await downloadCreditDenialPdf()
    downloadFile(`Statement of Credit Denial.pdf`, pdfData)
    setLoading(false)
  }

  const onCloseModal = (result: boolean, item: Record<string, any>) => {
    setShowPartyModal(false)
    if (!result) return
    let temp = cloneDeep(loanDetail.closeParties)
    temp.Lender = item
    dispatch(
      setLoanDetail({
        closeParties: temp,
      }),
    )
  }

  const onFedralAgency = async () => {
    const result: any = await prompt('Federal Agency', { value: data.federalAgency || defaultFederalAgency })
    if (result === false) return
    await onChange('federalAgency', result)
  }

  const onSendEmail = async () => {
    setLoading(true)
    const res = await addCreditDenialPdf()
    setLoading(false)
    if (res.success) {
      if (res.fileKey)
        setPrintEmailItem({
          files: [
            {
              id: Date.now(),
              name: 'Statement of Credit Denial.pdf',
              fileKey: res.fileKey,
              createdAt: Date.now(),
            },
          ],
        })
      setIsEmailOpen(true)
    }
  }

  const renderActions = () => {
    return (
      <div className="flex gap-2 items-center">
        <span
          className={`cursor-pointer flex justify-end italic text-shade-blue hover-shadow1 p-1`}
          onClick={onDownload}
        >
          <PrinterIcon className="w-5 h-5" />
        </span>
        <span className="text-shade-blue hover-shadow1 p-2 cursor-pointer" onClick={onSendEmail}>
          <EnvelopeIcon className="w-5 h-5" />
        </span>
      </div>
    )
  }

  return (
    <div className="py-6 px-2">
      <Overview title="Statement of Credit Denial" />
      <div className="max-w-screen-2xl m-auto">
        <div className="relative h-fit content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1 mb-4">
          <LayoutLoading show={isLoading} />
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center mb-5">Statement of Credit Denial</h2>
            {renderActions()}
          </div>
          <div className="grid gap-2 gap-y-4 md:grid-cols-4 sm:grid-cols-2 grid-cols-1">
            {Object.keys(inputs).map((key) => {
              const input = inputs[key]

              if (['AccountDescription', 'ActionTakenDescription'].includes(key)) {
                input.additionalElements = (
                  <span className="text-shade-blue cursor-pointer" onClick={() => onFill(key)}>
                    Fill
                  </span>
                )
              }

              if (inputs?.CreditDecision1?.value) {
                if (['CreditDecision2'].includes(key)) return
              } else {
                if (
                  [
                    'CreditDenialEquifax',
                    'CreditDenialTransUnion',
                    'CreditDenialExperian',
                    'CompanyName',
                    'CompanyAddress',
                    'CompanyPhone',
                  ].includes(key)
                )
                  return
              }
              const rtl = []
              if (
                !(
                  (inputs?.CreditDenialEquifax?.value ||
                    inputs?.CreditDenialTransUnion?.value ||
                    inputs?.CreditDenialExperian?.value) &&
                  ['CompanyName', 'CompanyAddress', 'CompanyPhone'].includes(key)
                )
              ) {
                rtl.push(
                  <div className={`col-span-${input.span || 1}`}>
                    <RenderInput
                      input={input}
                      Key={key}
                      onChange={onChange}
                      onBlur={onBlur}
                      showHistory={onShowHistory}
                    />
                  </div>,
                )
              }
              if (key === 'CreditDenialExperian') {
                rtl.push(
                  <div className="col-span-full grid grid-cols-4 gap-4 text-sm">
                    {inputs?.CreditDenialEquifax?.value && (
                      <p>
                        EQUIFAX MORTGAGE SERVICES
                        <br />
                        815 EAST GATE DR., SUITE 102
                        <br />
                        MOUNT LAUREL, NJ 08054
                        <br />
                        800-333-0037
                      </p>
                    )}
                    {inputs?.CreditDenialTransUnion?.value && (
                      <p>
                        TRANSUNION CORPORATION
                        <br />
                        2 BALDWIN PLACE
                        <br />
                        P.O. BOX 1000
                        <br />
                        CHESTER, PA 19022
                        <br />
                        800-888-4213
                      </p>
                    )}
                    {inputs?.CreditDenialExperian?.value && (
                      <p>
                        EXPERIAN
                        <br />
                        701 EXPERIAN PARKWAY
                        <br />
                        P.O. BOX 2002
                        <br />
                        ALLEN, TX 75013-0036
                        <br />
                        888-397-3742
                      </p>
                    )}
                  </div>,
                )
              }
              if (key === 'CreditDecision2') {
                rtl.push(
                  <div className="col-span-full flex gap-8 text-sm">
                    <div>
                      <p>If you have any questions, you should contact</p>
                      <span
                        className="text-shade-blue cursor-pointer underline"
                        onClick={() => setShowPartyModal(true)}
                      >
                        {lender.Company}
                        <br />
                        {lender.Street}
                        <br />
                        {lender.City}, {lender.State} {lender.Zip}
                        <br />
                        {phoneConvertor(lender.WorkPhone)}
                      </span>
                    </div>
                    <div>
                      <p>The federal agency that administers compliance is the</p>
                      <span className="text-shade-blue cursor-pointer underline" onClick={onFedralAgency}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: (data.federalAgency || defaultFederalAgency).replace(/\n/g, '<br />'),
                          }}
                        ></span>
                      </span>
                    </div>
                  </div>,
                )
              }
              if (key === 'CompanyPhone') {
                rtl.push(
                  <div className="col-span-full flex items-center gap-2">
                    <Checkbox
                      title="We also obtained your credit score from"
                      id="CreditScoreObtained"
                      value={data.CreditScoreObtained}
                      onChange={(value) => onChange('CreditScoreObtained', value)}
                      className="w-[unset]"
                    />
                    <div className="-mb-4">
                      <Select
                        id="CreditScoreType"
                        title=""
                        options={['Equifax', 'TransUnion', 'Experian']}
                        value={!data.CreditScoreObtained ? '' : data.CreditScoreType}
                        disabled={!data.CreditScoreObtained}
                        onChange={(value) => onChange('CreditScoreType', value)}
                        hasDefaultOption={true}
                      />
                    </div>
                    <span> and used it in making our credit decision</span>
                  </div>,
                )
              }
              if (key === 'Date') {
                rtl.push(<div className="col-span-1 flex justify-end items-center">{renderActions()}</div>)
              }
              return rtl
            })}
          </div>
        </div>
      </div>
      {isEmailOpen && (
        <AddNote
          onUpdate={onSubmit}
          onClose={() => setIsEmailOpen(false)}
          item={null}
          index={0}
          isEmail={true}
          replyData={null}
          loanNumber={loanNumber}
          defaultData={printEmailItem}
        />
      )}
      {showPartyModal && <PartyModal item={lender} onClose={onCloseModal} rolodexShow={true} />}
    </div>
  )
}
